import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, ArrowLeft, LogOut, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/brand/Logo";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";

const nav = [
  { to: "/super-admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/super-admin/organisations", label: "Organisations", icon: Building2 },
];

export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults(null); return; }
    const { data } = await api.get(`/super-admin/search?q=${encodeURIComponent(val)}`);
    setResults(data);
  };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 bg-[#0b1220] flex-col z-40 border-r border-white/5">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <LogoMark className="h-8 w-8" />
          <div><span className="font-display font-extrabold text-white">Pitso<span className="text-accent">IO</span></span><p className="text-[10px] text-accent uppercase tracking-wider font-bold">Control Plane</p></div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sa-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors mt-4">
            <ArrowLeft className="h-4 w-4" /> Exit to app
          </button>
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2"><p className="text-sm font-semibold text-white truncate">{user?.name}</p><p className="text-xs text-white/50">Super Admin</p></div>
          <button onClick={logout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 lg:pl-60 min-w-0">
        <header className="h-16 border-b border-border flex items-center px-5 sm:px-8 gap-4 sticky top-0 bg-background/80 backdrop-blur z-30">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search organisations, events, users…" className="pl-9" data-testid="platform-search" />
            {results && (
              <div className="absolute mt-2 w-full rounded-lg border border-border bg-card shadow-lg p-2 z-40 max-h-80 overflow-y-auto" data-testid="search-results">
                {["organisations", "events", "users"].map((k) => (
                  <div key={k} className="mb-2">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground px-2 py-1">{k}</p>
                    {results[k].length === 0 ? <p className="px-2 text-xs text-muted-foreground">None</p> : results[k].map((r, i) => (
                      <button key={i} onClick={() => { if (k === "organisations") { navigate(`/super-admin/organisations/${r.id}`); setResults(null); setQ(""); } }}
                        className="block w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted">{r.name} {r.email && <span className="text-muted-foreground text-xs">· {r.email}</span>}</button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>
        <main className="min-h-[calc(100vh-4rem)]"><Outlet /></main>
      </div>
    </div>
  );
}
